import React, { useCallback, useState } from "react";
import * as styles from "./SubscribeToNewsletterPopup.module.scss";
import Modal from "../../Modal/Modal";
import {
  Button,
  ModalBody,
  PartialSanityImageType
} from "@silversea-ssc/ui-components-react";
import NativeHeroImage from "../../NativeHeroImage/NativeHeroImage";
import { BlockContent } from "../../BlockContent";
import { SubscribeModalThankYouContent } from "../SubscribeModal/SubscribeModalThankYou";
import { ActualSubscribeModalForm } from "../SubscribeModal/SubscribeModalForm";
import { useIsTabletOrDesktop } from "src/utils/useIsTabletOrDesktop";
import SubscribeInlineForm from "../SubscribeInlineForm/SubscribeInlineForm";
import { FormDisclaimerFragment } from "types/graphql-types";
import { LeadFormData } from "components/DefaultSubscribeForm/types";
import { isPresent } from "src/utils/checks";
import SubscribeCrossBrandOptions, {
  LeadData
} from "components/SubscribeToNewsletter/SubscribeCrossBrandOptions/SubscribeCrossBrandOptions";
import { isEnvFeatureEnabled } from "src/utils/feature-flags";
import { INavLink } from "components/HeaderNavBar/type";
import { LeadAPIRequest } from "src/ssc-api/leadAPIClient";
import { LeadApiResponse } from "@silversea-ssc/dotcom-api-types/Lead";
import { blockContentToString } from "src/gatsby/algolia/blockContentToString";
import { useRemotePromo } from "src/ssc-api/useRemotePromos";
import { SubscribeFormContent } from "components/SubscribeToNewsletter/types";
import { isPromoValid } from "components/CustomPromo/promoValidation";

type Props = {
  isOpen: boolean;
  onRequestClose: () => void;
  onFormSubmitted: () => void;
  backgroundImage: PartialSanityImageType | undefined;
  subscribeFormContent: SubscribeFormContent;
  formDisclaimerContent: FormDisclaimerFragment | undefined;
  privacyPolicyUrl: INavLink;
  offerPageUrl: string | undefined;
  promoConfigurationCode?: string;
};

const SubscribeToNewsletterPopup: React.FC<Props> = ({
  isOpen,
  onRequestClose,
  onFormSubmitted,
  backgroundImage,
  subscribeFormContent,
  formDisclaimerContent,
  privacyPolicyUrl,
  offerPageUrl,
  promoConfigurationCode
}) => {
  const {
    headingTitle,
    headingContent,
    customHeadingTitle,
    customHeadingContent
  } = subscribeFormContent;
  const formName = "SubscribeToNewsletterPopup";
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedLeadData, setSubmittedLeadData] = useState<LeadData>();
  const remotePromo = useRemotePromo(promoConfigurationCode);
  const withCrossBrandSFO = isEnvFeatureEnabled(
    process.env.GATSBY_ENABLE_CROSS_BRAND_SFO
  );
  const onSubmitSuccess = useCallback(() => {
    setIsSubmitted(true);
    setIsSubmitted(true);
    if (onFormSubmitted) onFormSubmitted();
  }, [onFormSubmitted]);

  const onLeadFormSubmit = (
    formData: LeadFormData,
    request: LeadAPIRequest,
    response: LeadApiResponse
  ) => {
    setSubmittedLeadData({
      leadFormData: formData,
      leadApiResponse: response
    });
  };

  const isTabletOrDesktop = useIsTabletOrDesktop();
  const resetOnAfterOpen = () => {
    setIsSubmitted(false);
  };

  const isRemotePromoValid =
    isPresent(promoConfigurationCode) &&
    isPresent(remotePromo.data?.toDate) &&
    isPromoValid(promoConfigurationCode, remotePromo.data?.toDate);

  const title =
    isRemotePromoValid && customHeadingTitle
      ? customHeadingTitle
      : headingTitle;

  const content =
    isRemotePromoValid && customHeadingContent
      ? customHeadingContent
      : headingContent;

  const imageAlt = blockContentToString(title) || "";
  return (
    <Modal
      dataContentName="subscribe_to_newsletter_popup"
      modal={{
        isOpen: isOpen,
        onAfterOpen: resetOnAfterOpen,
        onRequestClose: isTabletOrDesktop ? onRequestClose : undefined,
        shouldCloseOnOverlayClick: false
      }}
      size="medium"
      className={styles.modal}
    >
      <ModalBody
        variant="full"
        paddingTop={false}
        paddingBottom={false}
        className={styles.body}
      >
        <div className={styles.background}>
          {backgroundImage && (
            <NativeHeroImage image={backgroundImage} alt={imageAlt} lazyload />
          )}
        </div>
        <div className={styles.boxWrapper}>
          <Button
            renderAs="button"
            variant="text"
            iconName="close"
            dataContentPiece="close-modal"
            className={styles.mobileCloseButton}
            onClick={onRequestClose}
          ></Button>

          <div className={styles.box}>
            {isSubmitted ? (
              <React.Suspense>
                <SubscribeModalThankYouContent
                  onRequestClose={onRequestClose}
                  variant="compact"
                  crossBrandEnabled={withCrossBrandSFO}
                  offerPageUrl={offerPageUrl}
                />
              </React.Suspense>
            ) : isPresent(submittedLeadData) ? (
              <SubscribeCrossBrandOptions
                onSubmitSuccess={onSubmitSuccess}
                leadData={submittedLeadData}
                variant={"compact"}
                privacyPolicyUrl={privacyPolicyUrl}
              />
            ) : (
              <React.Fragment>
                <div className={styles.title}>
                  <BlockContent
                    value={title}
                    tokens={remotePromo.data?.tokenValues}
                  />
                </div>
                <div>
                  <BlockContent
                    value={content}
                    tokens={remotePromo.data?.tokenValues}
                  />
                </div>

                <div>
                  <React.Fragment>
                    {isTabletOrDesktop ? (
                      <ActualSubscribeModalForm
                        onSubmitSuccess={
                          withCrossBrandSFO ? onLeadFormSubmit : onSubmitSuccess
                        }
                        formName={formName}
                        email=""
                        dataContentName="sfo_popup_form"
                        formDisclaimerContent={formDisclaimerContent}
                      />
                    ) : (
                      <SubscribeInlineForm
                        formTitle={title}
                        formContent={content}
                        formName={formName}
                        onRequestClose={onRequestClose}
                        onFormSubmitted={onSubmitSuccess}
                        formDisclaimerContent={formDisclaimerContent}
                        showCrossBrandOptions={true}
                        privacyPolicyUrl={privacyPolicyUrl}
                        offerPageUrl={offerPageUrl}
                        formVariant="default"
                        tokens={remotePromo.data?.tokenValues}
                      />
                    )}
                  </React.Fragment>
                </div>
              </React.Fragment>
            )}
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default SubscribeToNewsletterPopup;
